import { z } from 'zod';
import { rosterContact, MAX_ROSTER_CONTACTS, type RosterContactStatus } from './roster.js';
import { teamTimeZone } from './reminders.js';
import { signupInput } from './schemas.js';

/**
 * Clubs: a level above teams. A club registers (or a Hoopify admin makes one), is checked by
 * phone, then its admins and registrars set up teams, people and seasons. Club teams start as
 * drafts: nobody is emailed until the team goes live (now, or on a chosen date).
 */

export type ClubStatus = 'pending' | 'approved' | 'rejected' | 'suspended';
export type ClubRole = 'admin' | 'registrar';
export const CLUB_ROLE_LABELS: Record<ClubRole, string> = { admin: 'Club admin', registrar: 'Registrar' };
export const CLUB_ROLE_DESCRIPTIONS: Record<ClubRole, string> = {
  admin: 'Runs the club on Hoopify: teams, seasons, people, PlayHQ and club details.',
  registrar: 'Looks after people: the player list, putting players and coaches in teams, and sending invites.',
};

export type ClubPermission = 'club:read' | 'club:people' | 'club:manage';
export function clubPermissions(role: ClubRole): ClubPermission[] {
  return role === 'admin' ? ['club:read', 'club:people', 'club:manage'] : ['club:read', 'club:people'];
}

export const AU_STATES = ['ACT', 'NSW', 'NT', 'QLD', 'SA', 'TAS', 'VIC', 'WA'] as const;

/** Australian mobile, in any usual format; stored as +614XXXXXXXX. */
export const auMobile = z
  .string()
  .trim()
  .transform((v) => v.replace(/[\s()-]/g, ''))
  .transform((v) => (v.startsWith('+61') ? `0${v.slice(3)}` : v.startsWith('61') && v.length === 11 ? `0${v.slice(2)}` : v))
  .refine((v) => /^04\d{8}$/.test(v), 'Enter an Australian mobile number, like 0412 345 678.')
  .transform((v) => `+61${v.slice(1)}`);

export function formatMobile(e164: string) {
  const local = e164.startsWith('+61') ? `0${e164.slice(3)}` : e164;
  return local.length === 10 ? `${local.slice(0, 4)} ${local.slice(4, 7)} ${local.slice(7)}` : local;
}

const clubName = z.string().trim().min(2, 'Enter the club’s name.').max(80, 'Keep the name under 80 characters.');
const optText = (max: number, label: string) =>
  z
    .string()
    .trim()
    .max(max, `Keep the ${label} under ${max} characters.`)
    .nullish()
    .transform((v) => v || null);
const website = z
  .string()
  .trim()
  .max(200)
  .nullish()
  .transform((v) => (v ? (/^https?:\/\//i.test(v) ? v : `https://${v}`) : null))
  .refine((v) => !v || /^https?:\/\/[^\s.]+\.[^\s]+$/i.test(v), 'Enter a web address like hawksbasketball.com.au.');

/** A coach registering their club. They become its first admin once approved. */
export const registerClubInput = z.object({
  name: clubName,
  mobile: auMobile,
  suburb: optText(60, 'suburb'),
  state: z.enum(AU_STATES).nullish().transform((v) => v ?? null),
  website,
});
export type RegisterClubInput = z.input<typeof registerClubInput>;

/** New to Hoopify: make an account and register the club in one go (the club waits for the email to be confirmed). */
export const clubSignupInput = signupInput.extend({
  clubName,
  mobile: auMobile,
  suburb: optText(60, 'suburb'),
  state: z.enum(AU_STATES).nullish().transform((v) => v ?? null),
  website,
});
export type ClubSignupInput = z.input<typeof clubSignupInput>;

/** A Hoopify admin making a club: approved straight away, its contact invited as club admin. */
export const adminCreateClubInput = registerClubInput.extend({
  contactName: z.string().trim().min(1, 'Enter the contact’s name.').max(80),
  contactEmail: z.string().trim().toLowerCase().email('Enter a valid email address.').max(254),
});

export const updateClubInput = z.object({
  name: clubName.optional(),
  mobile: auMobile.optional(),
  suburb: optText(60, 'suburb').optional(),
  state: z.enum(AU_STATES).nullish().optional(),
  website: website.optional(),
  timeZone: teamTimeZone.optional(),
});

export const clubDecisionInput = z.object({
  decision: z.enum(['approve', 'reject', 'suspend', 'restore']),
  note: z.string().trim().max(500).nullish().transform((v) => v || null),
});

export interface ClubSummary {
  id: string;
  name: string;
  status: ClubStatus;
  role: ClubRole;
  suburb: string | null;
  state: string | null;
}

export interface ClubDetail extends ClubSummary {
  contactName: string;
  contactEmail: string;
  mobile: string;
  website: string | null;
  timeZone: string;
  permissions: ClubPermission[];
  decisionNote: string | null;
  playhq: { connected: boolean; tenant: string | null; orgId: string | null; syncedAt: string | null; error: string | null };
  counts: { teams: number; players: number; seasons: number };
}

/** The admin console's view of a club. */
export interface AdminClub {
  id: string;
  name: string;
  status: ClubStatus;
  contactName: string;
  contactEmail: string;
  mobile: string;
  suburb: string | null;
  state: string | null;
  website: string | null;
  registeredBy: string | null;
  decidedAt: string | null;
  decidedBy: string | null;
  decisionNote: string | null;
  admins: { name: string; email: string; role: ClubRole }[];
  /** False while the person who registered it hasn't confirmed their email address. */
  contactConfirmed: boolean;
  teamCount: number;
  createdAt: string;
}

// ---------- Club people ----------

export const clubInviteInput = z.object({
  email: z.string().trim().toLowerCase().email('Enter a valid email address.').max(254),
  role: z.enum(['admin', 'registrar']),
});

export interface ClubPerson {
  userId: string | null;
  inviteId: string | null;
  name: string | null;
  email: string;
  role: ClubRole;
  status: 'member' | 'invited';
}

export interface ClubInvitePreview {
  clubName: string;
  email: string;
  role: ClubRole;
  invitedBy: string;
  expired: boolean;
}

// ---------- Seasons ----------

const dateStr = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Use a date.');
export const clubSeasonInput = z
  .object({
    name: z.string().trim().min(1, 'Give the season a name, like Winter 2027.').max(60),
    startDate: dateStr.nullish().transform((v) => v ?? null),
    endDate: dateStr.nullish().transform((v) => v ?? null),
  })
  .refine((s) => !s.startDate || !s.endDate || s.startDate <= s.endDate, { message: 'The season has to end after it starts.', path: ['endDate'] });

export interface ClubSeason {
  id: string;
  name: string;
  startDate: string | null;
  endDate: string | null;
  teamCount: number;
}

export const copySeasonInput = z.object({
  fromSeasonId: z.string().uuid(),
  /** Add this to age groups like U12 (0 keeps them). */
  ageStep: z.union([z.literal(0), z.literal(1), z.literal(2)]).default(0),
});

// ---------- Teams ----------

export type TeamStatus = 'draft' | 'scheduled' | 'live';

export const clubTeamInput = z.object({
  name: z.string().trim().min(1, 'Enter a team name.').max(60),
  ageGroup: optText(40, 'age group'),
  clubSeasonId: z.string().uuid().nullish().transform((v) => v ?? null),
});
export const updateClubTeamInput = z.object({
  name: z.string().trim().min(1, 'Enter a team name.').max(60).optional(),
  ageGroup: optText(40, 'age group').optional(),
  clubSeasonId: z.string().uuid().nullish().optional(),
});

/** A coach on a club team: on the team, invited, or planned (sent when the team goes live). */
export interface ClubTeamStaff {
  userId: string | null;
  inviteId: string | null;
  name: string | null;
  email: string;
  role: 'head_coach' | 'assistant_coach' | 'viewer';
  status: 'member' | 'invited' | 'planned';
}

export interface ClubTeamPlayer {
  rosterPlayerId: string;
  clubPlayerId: string | null;
  name: string;
  number: string | null;
  contacts: { email: string; kind: 'player' | 'parent'; status: RosterContactStatus }[];
}

export interface ClubTeam {
  id: string;
  name: string;
  ageGroup: string | null;
  clubSeasonId: string | null;
  seasonName: string | null;
  status: TeamStatus;
  goLiveOn: string | null;
  playhq: { teamId: string; gradeName: string | null; syncedAt: string | null; error: string | null } | null;
  coaches: number;
  players: number;
}

export interface ClubTeamDetail extends ClubTeam {
  staff: ClubTeamStaff[];
  roster: ClubTeamPlayer[];
}

export const staffInput = z.object({
  email: z.string().trim().toLowerCase().email('Enter a valid email address.').max(254),
  role: z.enum(['head_coach', 'assistant_coach', 'viewer']),
});

export const assignPlayersInput = z.object({ clubPlayerIds: z.array(z.string().uuid()).min(1).max(60) });

/** Who'll be emailed when the team goes live. */
export interface SendPreview {
  coaches: number;
  players: number;
  parents: number;
  /** Of those, already have a Hoopify account (they get a notice to accept). */
  existing: number;
  emails: { email: string; role: 'head_coach' | 'assistant_coach' | 'viewer' | 'player' | 'parent' }[];
}

export const goLiveInput = z.discriminatedUnion('when', [
  z.object({ when: z.literal('now') }),
  z.object({ when: z.literal('date'), date: dateStr }),
  z.object({ when: z.literal('draft') }),
]);

// ---------- Club player list ----------

export const clubPlayerInput = z.object({
  name: z.string().trim().min(1, 'Enter the player’s name.').max(80),
  notes: optText(1000, 'note'),
  contacts: z
    .array(rosterContact)
    .max(MAX_ROSTER_CONTACTS, `Add up to ${MAX_ROSTER_CONTACTS} emails for a player.`)
    .refine((l) => new Set(l.map((c) => c.email)).size === l.length, 'That email is already on this player.')
    .default([]),
});
export const updateClubPlayerInput = clubPlayerInput.partial();

export interface ClubPlayer {
  id: string;
  name: string;
  notes: string | null;
  contacts: { email: string; kind: 'player' | 'parent' }[];
  teams: { teamId: string; teamName: string; seasonName: string | null }[];
}

export const bulkClubPlayersInput = z.object({ text: z.string().max(8000) });

// ---------- Existing teams joining ----------

export const clubTeamInviteInput = z.object({
  email: z.string().trim().toLowerCase().email('Enter the head coach’s email address.').max(254),
  clubSeasonId: z.string().uuid().nullish().transform((v) => v ?? null),
});

export const acceptClubTeamInput = z.object({ teamId: z.string().uuid() });

export interface MyClubTeamInvite {
  id: string;
  clubName: string;
  seasonName: string | null;
  invitedAt: string;
}

// ---------- PlayHQ ----------

export const playhqSettingsInput = z.object({
  tenant: z.string().trim().toLowerCase().regex(/^[a-z0-9-]{1,20}$/, 'Enter the sport’s short name, like bv for Basketball Victoria.'),
  orgId: z.string().trim().regex(/^[0-9a-f-]{32,36}$/i, 'The organisation ID is a long code like 1a2b3c4d-…; ask your association.'),
  /** Blank keeps the saved key. */
  apiKey: z.string().trim().max(200).nullish().transform((v) => v || null),
});

export interface PlayhqSeason {
  id: string;
  name: string;
  status: string | null;
  competition: string | null;
  association: string | null;
}

export interface PlayhqTeam {
  id: string;
  name: string;
  gradeId: string | null;
  gradeName: string | null;
  /** The Hoopify team already linked to it, if any. */
  linkedTeamId: string | null;
}

export const playhqImportInput = z.object({
  clubSeasonId: z.string().uuid().nullish().transform((v) => v ?? null),
  teams: z.array(z.object({ id: z.string().min(1).max(64), name: z.string().min(1).max(80), gradeId: z.string().max(64).nullish(), gradeName: z.string().max(120).nullish() })).min(1).max(60),
});

export const playhqLinkInput = z.object({
  playhqTeamId: z.string().min(1).max(64).nullable(),
  gradeId: z.string().max(64).nullish(),
  gradeName: z.string().max(120).nullish(),
});

export interface LadderRow {
  position: number;
  team: string;
  played: number | null;
  won: number | null;
  lost: number | null;
  points: number | null;
  isUs: boolean;
}

export interface TeamLadder {
  gradeName: string | null;
  rows: LadderRow[];
  syncedAt: string | null;
}

/** Bump an age group like "U12" by n (others unchanged). */
export function bumpAgeGroup(ageGroup: string | null, n: number) {
  if (!ageGroup || !n) return ageGroup;
  return ageGroup.replace(/(U|Under\s*)(\d{1,2})/i, (_, p: string, d: string) => `${p}${Number(d) + n}`);
}
